/**
 * Authentication state store using Pinia
 * Provides reactive authentication state that components can watch
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useAuthStore = defineStore('auth', () => {
  // State
  const isLoggedOut = ref(false);
  const logoutTimestamp = ref(null);
  /** Message to show on login page after logout due to TOKEN_REUSE_DETECTED or requiresReLogin */
  const logoutSecurityMessage = ref(null);

  // Getters
  const isAuthenticated = computed(() => !isLoggedOut.value);
  const shouldPreventApiCall = computed(() => isLoggedOut.value);

  // Actions
  function markAsLoggedOut() {
    isLoggedOut.value = true;
    logoutTimestamp.value = Date.now();
  }

  function markAsLoggedIn() {
    isLoggedOut.value = false;
    logoutTimestamp.value = null;
    logoutSecurityMessage.value = null;
  }

  function setLogoutSecurityMessage(message) {
    logoutSecurityMessage.value = message;
  }

  /**
   * Check if an error should be suppressed (expected after logout)
   * @param {Error} error - The error to check
   * @param {Object} router - Vue router instance (optional)
   * @returns {boolean} True if error should be suppressed
   */
  function shouldSuppressError(error, router = null) {
    // If user is logged out, suppress ALL errors (not just 401)
    // This prevents error messages from appearing after logout when
    // pending API requests complete or components are still mounted
    if (isLoggedOut.value) {
      return true;
    }

    // Also suppress on auth pages (even if not explicitly logged out)
    if (router) {
      try {
        const currentRoute = router.currentRoute?.value;
        if (!currentRoute) return false;
        
        const isAuthPage = currentRoute.name === 'auth1.sign-in1' || 
                          currentRoute.name === 'auth1.sign-up1' || 
                          currentRoute.name === 'auth1.recoverpassword1' || 
                          currentRoute.name === 'auth1.verify-account' || 
                          currentRoute.name === 'auth1.delete-user' ||
                          currentRoute.name === 'auth1.complete-registration';
        
        if (isAuthPage) {
          const errorMessage = (error?.response?.data?.message || '').toLowerCase();
          const isNoTokenError = errorMessage.includes('no token found') || 
                                errorMessage.includes('missing token') ||
                                error?.response?.status === 401;
          
          if (isNoTokenError) {
            return true;
          }
        }
      } catch (e) {
        // Router not available or error accessing route, don't suppress
        return false;
      }
    }

    return false;
  }

  /**
   * Check if error messages should be displayed to the user
   * This is a convenience method for components to check before showing toasts
   * @returns {boolean} True if errors should be shown, false if user is logged out
   */
  function shouldShowErrors() {
    return !isLoggedOut.value;
  }

  return {
    // State
    isLoggedOut,
    logoutTimestamp,
    logoutSecurityMessage,
    // Getters
    isAuthenticated,
    shouldPreventApiCall,
    // Actions
    markAsLoggedOut,
    markAsLoggedIn,
    setLogoutSecurityMessage,
    shouldSuppressError,
    shouldShowErrors,
  };
});


